import React, { useEffect, useState } from 'react';
import { sanitizeNotificationDisplayText, sanitizeRichBodyHtmlForPreview } from '../../../lib/hubupNotificationFeedBrand';
import { resolveHubupAssetUrl } from '../../../api/hubup';
import {
  fetchHubupCommunitiesForPicker,
  fetchNotificationsSnapshot,
} from '../utils/hubupWebCreator';

function CompactNotifCard({ item, compact }) {
  const title = sanitizeNotificationDisplayText(item.title || item.topicTitle || 'Notification');
  const bodyHtml = item.bodyHtml ? sanitizeRichBodyHtmlForPreview(item.bodyHtml) : '';
  const iconSrc = resolveHubupAssetUrl(item.groupIconUrl);

  return (
    <article
      className={`min-h-0 shrink overflow-hidden rounded-xl border border-[#E8E4FF] bg-[#EEF1FA]/90 px-3 py-2 ${
        compact ? 'flex-1 basis-0' : ''
      }`}
    >
      <div className="flex items-start gap-2 min-h-0 h-full">
        <div className="w-8 h-8 shrink-0 rounded-lg overflow-hidden border border-[#E8E4FF] bg-white">
          {iconSrc ? (
            <img src={iconSrc} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-sm">📢</div>
          )}
        </div>
        <div className="min-w-0 flex-1 overflow-hidden">
          <div className="flex items-center gap-1 min-w-0">
            <p className="text-[11px] font-semibold text-[#333483] truncate">{item.feedName}</p>
            {item.priority === 'high' ? (
              <span className="text-[9px] px-1 rounded bg-red-100 text-red-700 shrink-0">!</span>
            ) : null}
          </div>
          <p className="text-sm font-bold text-[#1E1B4B] line-clamp-2 leading-snug mt-0.5">{title}</p>
          {bodyHtml ? (
            <div
              className="text-xs text-[#333483]/90 line-clamp-2 mt-0.5 [&_p]:inline [&_p]:m-0 [&_a]:pointer-events-none"
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
            />
          ) : null}
        </div>
      </div>
    </article>
  );
}

function NotificationsList({ items = [], count = 3 }) {
  const limit = Math.max(1, Math.min(3, Number(count) || 3));
  const visible = items.slice(0, limit);
  const compact = limit > 1;

  if (!visible.length) {
    return (
      <div className="w-full h-full min-h-0 flex items-center justify-center text-center text-xs text-muted-foreground px-3">
        <div>
          <p className="font-medium">Notifications HubUP</p>
          <p className="mt-1 opacity-80">Aucune notification à afficher</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full min-h-0 max-h-full overflow-hidden flex flex-col gap-2 box-border">
      {visible.map((item, index) => (
        <CompactNotifCard key={item.id || `${item.title}-${index}`} item={item} compact={compact} />
      ))}
    </div>
  );
}

export default function HubupNotificationsView({ comp, liveFetch = false }) {
  const props = comp.props || {};
  const count = props.notificationCount || 3;
  const cached = props.cachedNotifications || [];
  const [liveItems, setLiveItems] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!liveFetch || !props.groupId) {
      setLiveItems(null);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const communities = await fetchHubupCommunitiesForPicker();
        const community = communities.find((c) => c.id === props.groupId);
        const snapshot = await fetchNotificationsSnapshot(
          props.groupId,
          props.topicId || '',
          count,
          community,
        );
        if (!cancelled) setLiveItems(snapshot.cachedNotifications || []);
      } catch {
        if (!cancelled) setLiveItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [liveFetch, props.groupId, props.topicId, count]);

  const items = liveFetch && liveItems != null ? liveItems : cached;

  if (loading && !items.length) {
    return (
      <div className="w-full h-full min-h-0 flex items-center justify-center text-xs text-muted-foreground">
        Chargement…
      </div>
    );
  }

  return <NotificationsList items={items} count={count} />;
}
